import React, { useCallback, useRef, useState, useEffect } from "react";
import { CartItem, Product, Variant } from "@pearson-ell/commerce-sdk";
import { onInputDebounce } from "../../commons/utils";
import { DEBOUNCE_INTERVAL, ALLOWED_KEYS } from "../../commons/constants";
import { Message } from "../Message";
import { setAriaInvalidAttributes } from "../../commons/utils";
import { SelectVariant } from "../SelectVariant/SelectVariant";
import { StyledInput } from "../CartProduct/CartProduct.parts";

export interface ItemQuantityProps {
    item: CartItem | Product;
    onChange?: (
        ev: React.ChangeEvent<HTMLInputElement>,
        item?: CartItem
    ) => void | Promise<void>;
    onChangeVariants?: (variant: Variant | undefined) => void;
    debounceChangeQty?: number;
    isBusy?: boolean;
    variants?: Variant[] | undefined;
};

export const ItemQuantity: React.FC<ItemQuantityProps> = ({
    item,
    onChange,
    onChangeVariants,
    debounceChangeQty = DEBOUNCE_INTERVAL,
    isBusy = false,
    variants,
}) => {
    const minPurchaseQuantity = item.minPurchaseQuantity || 1;
    const maxPurchaseQuantity = item.maxPurchaseQuantity;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const isCartItem = (item as CartItem).productId !== undefined;
    const [value, setValue] = useState(
        isCartItem ? (item as CartItem).quantity : minPurchaseQuantity
    );
    const [isInvalid, setIsInvalid] = useState(false);
    const [isDisabled, setIsDisabled] = useState(maxPurchaseQuantity === 1);
    const [message, setMessage] = useState({ text: "", type: "" });

    useEffect(() => {
        setIsDisabled(isBusy || maxPurchaseQuantity === 1);
    }, [isBusy]);

    useEffect(() => {
        setAriaInvalidAttributes(inputRef, isInvalid, "errorMessage");
    }, [isInvalid]);

    const onKeydown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (!ALLOWED_KEYS.includes(event.key)) {
                event.preventDefault();
            }
        },
        []
    );

    const onKeyup = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            const value = event.currentTarget.value;
            if (value.includes(".")) {
                event.currentTarget.value = `${Number.parseInt(value)}`;
            }
        },
        []
    );

    const onInputChange = (
        ev: React.ChangeEvent<HTMLInputElement>,
        item?: CartItem
    ) => {
        ev.preventDefault();
        let numValue = Number.parseInt(ev.target.value);
        numValue = Number.isNaN(numValue) ? 0 : numValue;
        if (
            numValue >= minPurchaseQuantity &&
            (numValue <= maxPurchaseQuantity || !maxPurchaseQuantity)
        ) {
            setIsInvalid(false);
            setMessage({ text: "", type: "" });
        } else if (numValue < minPurchaseQuantity) {
            setIsInvalid(true);
            setMessage({
                text: `Min qty is ${minPurchaseQuantity}`,
                type: "Error",
            });
        } else if (numValue > maxPurchaseQuantity) {
            setIsInvalid(true);
            setMessage({
                text: `Max qty is ${maxPurchaseQuantity}`,
                type: "Error",
            });
        }
        if (onChange) {
            onChange(ev, item);
        }
        setValue(numValue);
    };

    const onInputDebounceChange = useCallback(
        onInputDebounce(onInputChange, debounceChangeQty),
        [onInputChange]
    );

    return (
        <StyledInput className={"input"}>
            {variants && variants?.length > 0 ? (
                <SelectVariant
                    onChange={onChangeVariants}
                    className={"variants"}
                    variants={variants}
                ></SelectVariant>
            ) : null}
            <input
                ref={inputRef}
                type="number"
                onKeyDown={onKeydown}
                onKeyUp={onKeyup}
                className={
                    message.type === "Error"
                        ? "cart-product-input error"
                        : "cart-product-input"
                }
                disabled={isDisabled}
                defaultValue={value}
                min={minPurchaseQuantity}
                max={maxPurchaseQuantity}
                onChange={(ev) => {
                    return isCartItem
                        ? onInputDebounceChange(ev, item as CartItem)
                        : onInputChange(ev);
                }}
                aria-label="Quantity of product"
            />
            {isInvalid && (
                <Message
                    id="errorMessage"
                    text={message.text}
                    type={message.type}
                />
            )}
        </StyledInput>
    );
};
